// Shared search for the business-data pages (Objection Handling, Document
// Library): a query matches on the exact word, its other forms (price, prices,
// pricing, priced) and its synonyms (cost, fee, rate, value...). Pure
// functions, client-side, no server round trip.
//
// A query word that appears in a group searches for every word in that group.
// Entries may be phrases. Edit the list freely; it is the whole vocabulary.
export const SYNONYM_GROUPS = [
  // No "rate" here: in a bank's library it is the interest rate and appears in
  // nearly every objection, so a search for "cost" would match everything.
  ['cost', 'costs', 'price', 'pricing', 'fee', 'fees', 'value', 'budget', 'expensive', 'affordable', 'charge', 'charges', 'dollars'],
  // No "timing" (it stems to "tim", the same as "time") and no "when": both
  // appear in most objections, so a timeline search lit up the whole library.
  ['timeline', 'schedule', 'how long', 'duration', 'rollout', 'deadline'],
  ['implementation', 'implement', 'onboarding', 'onboard', 'setup', 'set up', 'launch', 'go-live', 'deploy', 'install'],
  ['contract', 'agreement', 'terms', 'sow', 'msa', 'renewal', 'cancel', 'cancellation', 'termination', 'commitment'],
  ['security', 'secure', 'compliance', 'compliant', 'privacy', 'data', 'soc', 'audit', 'encryption'],
  ['support', 'service', 'customer success', 'help', 'training', 'assistance'],
  ['integration', 'integrate', 'api', 'connect', 'sync', 'crm', 'compatible'],
  ['roi', 'return', 'results', 'payback', 'outcome', 'benefit', 'savings'],
  ['competitor', 'competitors', 'alternative', 'alternatives', 'compare', 'comparison', 'versus', 'vs', 'instead'],
  ['risk', 'guarantee', 'liability', 'warranty', 'trust', 'reliable', 'proven'],
  ['team', 'staff', 'headcount', 'reps', 'users', 'seats', 'people', 'employees'],
  ['demo', 'trial', 'pilot', 'test drive', 'proof of concept', 'evaluation'],
];

const STOP_WORDS = new Set(['a', 'an', 'the', 'of', 'to', 'for', 'and', 'or', 'in', 'on', 'is', 'are', 'we', 'our', 'your', 'do', 'does', 'it', 'with']);

// A light stemmer: the possessive, then a plural, then a verb suffix, then a
// trailing "e", each only while at least three characters remain. price /
// prices / pricing / priced all become "pric"; meeting / meetings "meet";
// process / processing / processes "process"; rate / rates "rat"; "costume"
// stays "costum", so a search for "cost" does not find it. Plural and verb
// suffixes come off in separate steps so "meetings" and "savings" reach the
// same stem as "meeting" and "saving".
const keeps = (w, cut) => w.length - cut >= 3;
export const stem = (word) => {
  let w = String(word || '').toLowerCase();
  if (w.endsWith("'s")) w = w.slice(0, -2);
  if (w.endsWith('ies') && w.length - 2 >= 3) w = `${w.slice(0, -3)}y`;
  else if (/(ss|sh|ch|x|z)es$/.test(w) && keeps(w, 2)) w = w.slice(0, -2);
  else if (w.endsWith('s') && !/(ss|us)$/.test(w) && keeps(w, 1)) w = w.slice(0, -1);
  if (w.endsWith('ing') && keeps(w, 3)) w = w.slice(0, -3);
  else if (w.endsWith('ed') && keeps(w, 2)) w = w.slice(0, -2);
  if (w.endsWith('e') && keeps(w, 1)) w = w.slice(0, -1);
  return w;
};

const WORD = /[a-z0-9]+(?:'[a-z0-9]+)?/gi;
const tokens = (text) => String(text || '').toLowerCase().match(WORD) || [];
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A term is one word (matched by stem) or a phrase (matched as consecutive
// words with anything non-alphanumeric between them).
const toTerm = (entry) => {
  const words = tokens(entry);
  return words.length <= 1
    ? { text: entry, stem: stem(words[0] || entry), phrase: null }
    : { text: entry, stem: null, phrase: new RegExp(words.map(escapeRegex).join('[^a-z0-9]+'), 'gi') };
};

// stem of a single-word entry -> every term in its group. Phrase entries are
// reachable through their group's single words.
const GROUP_BY_STEM = new Map();
SYNONYM_GROUPS.forEach((group) => {
  const terms = group.map(toTerm);
  group.forEach((entry) => {
    const words = tokens(entry);
    if (words.length === 1) GROUP_BY_STEM.set(stem(words[0]), terms);
  });
});

// The query as a list of words, each carrying the terms that satisfy it.
// Stop words are dropped; a query made only of stop words is searched as it
// was typed. Every word must be satisfied (AND), by any of its terms (OR).
// The last word is usually still being typed, so when it belongs to no
// synonym group it also matches as a prefix ("sched" finds "schedule");
// group words keep their exact-stem rule so "cost" never finds "costume".
// A query with characters but no searchable word ("---") matches nothing.
export const expandQuery = (query) => {
  const raw = String(query || '').trim();
  const words = tokens(query).map((w) => w.replace(/^'+|'+$/g, '')).filter(Boolean);
  const kept = words.filter((w) => !STOP_WORDS.has(w));
  const use = kept.length ? kept : (words.length ? [words.join(' ')] : []);
  if (use.length === 0) return raw ? [{ word: raw, terms: [] }] : [];
  return use.map((word, i) => {
    const own = toTerm(word);
    const group = own.stem !== null ? GROUP_BY_STEM.get(own.stem) : null;
    const seen = new Set([own.text]);
    const terms = [own];
    (group || []).forEach((t) => { if (!seen.has(t.text)) { seen.add(t.text); terms.push(t); } });
    const prefix = i === use.length - 1 && own.stem !== null && !group && !STOP_WORDS.has(word) && word.length >= 2;
    return prefix ? { word, terms, prefix: true } : { word, terms };
  });
};

// Phrase regexes are shared and global, so their lastIndex is reset before
// every use; a leftover position from an earlier test() would skip a hit.
const phraseMatches = (term, lower) => {
  term.phrase.lastIndex = 0;
  const hit = term.phrase.test(lower);
  term.phrase.lastIndex = 0;
  return hit;
};
const termHits = (term, lower, stems) => (term.phrase ? phraseMatches(term, lower) : stems.has(term.stem));

export const textMatches = (text, expanded) => {
  if (!expanded || expanded.length === 0) return true;
  const lower = String(text || '').toLowerCase();
  const textWords = tokens(lower);
  const stems = new Set(textWords.map(stem));
  return expanded.every(({ word, terms, prefix }) =>
    terms.some((t) => termHits(t, lower, stems)) || (prefix === true && textWords.some((w) => w.startsWith(word))));
};

// Character ranges [start, end) of the matched words and phrases in `text`,
// merged and in order, for highlighting. Whole words, so a hit on "pric"
// highlights all of "pricing".
export const matchRanges = (text, expanded) => {
  const source = String(text || '');
  if (!expanded || expanded.length === 0 || !source) return [];
  const lower = source.toLowerCase();
  const ranges = [];
  const wordTerms = new Set();
  const phraseTerms = [];
  const prefixes = expanded.filter((x) => x.prefix === true).map((x) => x.word);
  expanded.forEach(({ terms }) => terms.forEach((t) => (t.phrase ? phraseTerms.push(t) : wordTerms.add(t.stem))));
  if (wordTerms.size || prefixes.length) {
    for (const m of lower.matchAll(WORD)) {
      if (wordTerms.has(stem(m[0])) || prefixes.some((p) => m[0].startsWith(p))) ranges.push([m.index, m.index + m[0].length]);
    }
  }
  phraseTerms.forEach((t) => {
    t.phrase.lastIndex = 0;
    for (const m of lower.matchAll(t.phrase)) ranges.push([m.index, m.index + m[0].length]);
    t.phrase.lastIndex = 0;
  });
  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged = [];
  ranges.forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  });
  return merged;
};
